/**
 * Terminal WebSocket Connection
 *
 * Handles WebSocket communication with the terminal server.
 * Each connection is bound to exactly one terminal session.
 *
 * Connection URLs:
 *   ws://host:port/terminals/new   - Create a new terminal
 *   ws://host:port/terminals/:id   - Attach to existing terminal
 *
 * Binary frames are raw terminal I/O (no framing needed).
 * Text frames are JSON control messages.
 */

import {
  type AutoCreateTerminalOptions,
  type ClientMessage,
  ServerMessage,
  type TerminalId,
  type TerminalOptions,
  type TerminalSession,
  type TerminalSize,
} from "@modules/terminal-common";
import type { ConnectionState, TerminalClientOptions } from "./types";

const DEFAULT_RECONNECT_DELAY_MS = 1000;
const DEFAULT_KEEPALIVE_INTERVAL_MS = 30000;

/** Build WebSocket URL for terminal connection */
function buildWebSocketUrl(
  baseUrl: string,
  terminalId?: TerminalId,
  options?: AutoCreateTerminalOptions,
  upsert?: boolean,
): string {
  const url = new URL(baseUrl);

  // Add options as query params
  if (options?.cwd) url.searchParams.set("cwd", options.cwd);
  if (options?.env) {
    for (const [key, value] of Object.entries(options.env)) {
      url.searchParams.append("env", `${key}=${value}`);
    }
  }
  if (options?.args) {
    for (const arg of options.args) {
      url.searchParams.append("args", arg);
    }
  }
  if (options?.shell) url.searchParams.set("shell", options.shell);
  if (options?.size) {
    url.searchParams.set("cols", String(options.size.cols));
    url.searchParams.set("rows", String(options.size.rows));
  }

  if (terminalId) {
    // Attach to existing terminal
    url.pathname = `/terminals/${terminalId}`;
    if (upsert) url.searchParams.set("upsert", "true");
  } else {
    // Create new terminal
    url.pathname = "/terminals/new";
    if (options?.id) url.searchParams.set("id", options.id);
  }

  return url.toString();
}

function toBinaryPayload(data: unknown): Uint8Array | null {
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }

  if (
    data instanceof ArrayBuffer ||
    Object.prototype.toString.call(data) === "[object ArrayBuffer]"
  ) {
    return new Uint8Array(data as ArrayBuffer);
  }

  return null;
}

export class TerminalConnection {
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = "disconnected";
  private session: TerminalSession | null = null;
  private keepaliveInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private exited = false;
  private isFirstMessage = true;

  private readonly baseUrl: string;
  private readonly initialTerminalId?: TerminalId;
  private readonly createOptions?: TerminalOptions;
  private readonly upsert: boolean;
  private readonly onData?: (data: Uint8Array, isFirstMessage: boolean) => void;
  private readonly onExit?: (exitCode: number) => void;
  private readonly onConnectionChange?: (state: ConnectionState) => void;
  private readonly onSession?: (session: TerminalSession) => void;
  private readonly onError?: (error: Error) => void;
  private readonly onReconnecting?: () => void;
  private readonly autoReconnect: boolean;
  private readonly reconnectDelayMs: number;
  private readonly keepaliveIntervalMs: number;

  constructor(options: TerminalClientOptions) {
    this.baseUrl = options.url;
    this.initialTerminalId = options.terminalId;
    this.createOptions = options.createOptions;
    this.upsert = options.upsert ?? false;
    this.onData = options.onData;
    this.onExit = options.onExit;
    this.onConnectionChange = options.onConnectionChange;
    this.onSession = options.onSession;
    this.onError = options.onError;
    this.onReconnecting = options.onReconnecting;
    this.autoReconnect = options.autoReconnect ?? true;
    this.reconnectDelayMs =
      options.reconnectDelayMs ?? DEFAULT_RECONNECT_DELAY_MS;
    this.keepaliveIntervalMs =
      options.keepaliveIntervalMs ?? DEFAULT_KEEPALIVE_INTERVAL_MS;
  }

  connect(): void {
    if (this.disposed || this.ws) return;

    this.setConnectionState("connecting");

    try {
      // If attaching to an existing terminal, clear before scrollback replay
      if (this.initialTerminalId) {
        this.onReconnecting?.();
      }

      // Build URL with terminal ID or creation options
      const wsUrl = buildWebSocketUrl(
        this.baseUrl,
        this.initialTerminalId,
        this.createOptions,
        this.upsert,
      );
      this.ws = new WebSocket(wsUrl);
      this.ws.binaryType = "arraybuffer";

      this.ws.onopen = () => {
        // Connection established, wait for "connected" message from server
      };

      this.ws.onclose = () => {
        this.handleDisconnect();
      };

      this.ws.onerror = () => {
        this.onError?.(new Error("WebSocket error"));
        this.setConnectionState("error");
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    } catch (error) {
      this.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.setConnectionState("error");
    }
  }

  disconnect(): void {
    this.stopKeepalive();
    this.clearReconnectTimeout();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setConnectionState("disconnected");
  }

  dispose(): void {
    this.disposed = true;
    this.disconnect();
  }

  write(data: string | Uint8Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Send raw binary data (no framing needed - one terminal per socket)
    const bytes =
      typeof data === "string" ? new TextEncoder().encode(data) : data;
    this.ws.send(bytes);
  }

  resize(size: TerminalSize): void {
    this.send({ type: "resize", size });
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  getSession(): TerminalSession | null {
    return this.session;
  }

  getTerminalId(): TerminalId | null {
    return this.session?.id ?? null;
  }

  private send(message: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(message));
  }

  private handleMessage(data: ArrayBuffer | string): void {
    const binary = toBinaryPayload(data);

    // Binary frame: raw terminal data
    if (binary) {
      this.onData?.(binary, this.isFirstMessage);
      this.isFirstMessage = false;
      return;
    }

    if (typeof data !== "string") {
      this.onError?.(new Error("Unsupported server message type"));
      return;
    }

    // Text frame: JSON control message
    try {
      const message = ServerMessage.assert(JSON.parse(data));
      this.handleServerMessage(message);
    } catch {
      this.onError?.(new Error("Failed to parse server message"));
    }
  }

  private handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case "connected":
        this.session = message.session;
        this.setConnectionState("connected");
        this.isFirstMessage = true;
        this.onSession?.(this.session);
        this.startKeepalive();
        break;

      case "exit":
        this.exited = true;
        this.onExit?.(message.exitCode);
        break;

      case "error":
        this.onError?.(new Error(`${message.code}: ${message.message}`));
        break;
    }
  }

  private handleDisconnect(): void {
    this.stopKeepalive();
    this.ws = null;

    if (this.disposed) {
      this.setConnectionState("disconnected");
      return;
    }

    // Can retry if:
    // 1. Have a session (can reattach by ID)
    // 2. Have an initialTerminalId (can retry initial connection)
    const canRetry = this.session || this.initialTerminalId;

    if (this.autoReconnect && canRetry && !this.exited) {
      this.setConnectionState("connecting");
      this.scheduleReconnect();
    } else {
      this.setConnectionState("disconnected");
    }
  }

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.onConnectionChange?.(state);
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepaliveInterval = setInterval(() => {
      this.send({ type: "keepalive" });
    }, this.keepaliveIntervalMs);
  }

  private stopKeepalive(): void {
    if (this.keepaliveInterval) {
      clearInterval(this.keepaliveInterval);
      this.keepaliveInterval = null;
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout();
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;

      if (this.session) {
        // Reconnect to the same terminal by ID
        // Notify that we're about to reconnect (allows clearing terminal before scrollback replay)
        this.onReconnecting?.();
        const wsUrl = buildWebSocketUrl(this.baseUrl, this.session.id);
        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = "arraybuffer";
        this.ws.onopen = () => {};
        this.ws.onclose = () => this.handleDisconnect();
        this.ws.onerror = () => {
          this.onError?.(new Error("WebSocket error"));
          this.setConnectionState("error");
        };
        this.ws.onmessage = (event) => this.handleMessage(event.data);
      } else {
        // Never got a session (initial connection failed), retry connect()
        this.connect();
      }
    }, this.reconnectDelayMs);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}
